import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Boxes, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, EmptyState, Loading } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const empty = { name: "", quantity: "", unit: "kg", reorder_level: "", cost_per_unit: "", category: "General" };

export default function Inventory() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get("/inventory").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...it, quantity: String(it.quantity), reorder_level: String(it.reorder_level), cost_per_unit: String(it.cost_per_unit) });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form, quantity: parseFloat(form.quantity) || 0,
      reorder_level: parseFloat(form.reorder_level) || 0, cost_per_unit: parseFloat(form.cost_per_unit) || 0,
    };
    try {
      if (editing) await api.put(`/inventory/${editing.id}`, payload);
      else await api.post("/inventory", payload);
      toast.success(editing ? "Item updated" : "Item added");
      setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/inventory/${id}`); toast.success("Deleted"); load();
  };

  if (!items) return <Loading />;
  const lowCount = items.filter((i) => i.quantity <= i.reorder_level).length;

  return (
    <div data-testid="inventory-page">
      <PageHeader title="Inventory" subtitle="Track stock levels and get low-stock alerts."
        action={<Button data-testid="add-inventory-btn" onClick={openNew} className="bg-primary hover:bg-[#B35A42] text-primary-foreground"><Plus className="h-4 w-4 mr-1" /> Add item</Button>} />

      {lowCount > 0 && (
        <div className="mb-6 bg-accent/40 border border-accent rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-primary" />
          <p className="text-sm"><span className="font-semibold">{lowCount}</span> item(s) at or below reorder level.</p>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState icon={Boxes} title="No inventory items" desc="Add ingredients and supplies to start tracking stock."
          action={<Button onClick={openNew} className="bg-primary text-primary-foreground"><Plus className="h-4 w-4 mr-1" /> Add item</Button>} />
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Item</TableHead><TableHead>Category</TableHead>
                <TableHead className="text-right">In stock</TableHead>
                <TableHead className="text-right">Reorder at</TableHead>
                <TableHead className="text-right">Cost/unit</TableHead>
                <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => {
                const low = it.quantity <= it.reorder_level;
                return (
                  <TableRow key={it.id} data-testid={`inventory-row-${it.id}`}>
                    <TableCell className="font-medium">{it.name}</TableCell>
                    <TableCell className="text-muted-foreground">{it.category}</TableCell>
                    <TableCell className="text-right">{it.quantity} {it.unit}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{it.reorder_level}</TableCell>
                    <TableCell className="text-right">${Number(it.cost_per_unit).toFixed(2)}</TableCell>
                    <TableCell>
                      {low ? <Badge className="bg-primary/15 text-primary border-0 hover:bg-primary/15">Low</Badge>
                        : <Badge className="bg-secondary/15 text-secondary border-0 hover:bg-secondary/15">OK</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(it)} data-testid={`edit-inventory-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(it.id)} data-testid={`delete-inventory-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Edit item" : "Add inventory item"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label>
              <Input data-testid="inv-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Quantity</Label>
                <Input data-testid="inv-form-qty" type="number" step="0.01" required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Unit</Label>
                <Input data-testid="inv-form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Reorder level</Label>
                <Input type="number" step="0.01" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Cost per unit ($)</Label>
                <Input type="number" step="0.01" value={form.cost_per_unit} onChange={(e) => setForm({ ...form, cost_per_unit: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Category</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <DialogFooter>
              <Button type="submit" data-testid="inv-form-submit" className="bg-primary hover:bg-[#B35A42] text-primary-foreground">{editing ? "Save changes" : "Add item"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
